import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';
import ChevronUp from '@decisiv/iconix/lib/components/ChevronUp';
import Times from '@decisiv/iconix/lib/components/Times';

import RequiredIcon from '@decisiv/ui-components/lib/atoms/RequiredIcon';
import Message from '@decisiv/ui-components/lib/components/Message';
import Popover from '@decisiv/ui-components/lib/components/Popover';
import Tag from '@decisiv/ui-components/lib/components/Tag';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import SelectableOptionsList from '../SelectableOptionsList';
import useStagedSelection from '../useStagedSelection';
import {
  AccountsChevron,
  AccountsEmpty,
  AccountsInput,
  AccountsLabel,
  AccountsPanel,
  AccountsTags,
  AccountsStatus,
  AccountsTrigger,
  AccountsTriggerBody,
  FieldGroup,
} from './styles';

/**
 * How close to the bottom counts as "fetch the next page".
 *
 * Roughly two 30px rows, so the next page is usually in place before the admin
 * reaches the end rather than after they hit it.
 */
const LOAD_MORE_THRESHOLD_PX = 60;

/**
 * A searchable, multi-select Account picker, staged behind APPLY.
 *
 * Not a KeyDS Select — Select is single-select — and not a KeyDS Combobox
 * either: Combobox passes `showCheckbox: !multiple` to its dropdown, so a
 * multi-select Combobox renders no checkboxes, and it exposes no way to add an
 * APPLY / CANCEL / CLEAR footer. So this is the same Popover + OptionsList
 * arrangement the users list's FilterMenu uses, sharing its staged-draft logic,
 * behind a field built to look and behave like a Select.
 *
 * Filtering happens by typing in the field itself, not through a search box
 * inside the menu.
 *
 * Accounts are assumed searched and paged server side: an admin can administer
 * thousands, so `options` is however many pages have been loaded, not all of
 * them. The caller owns the paging and debouncing (see useAccountSearch); this
 * asks for the next page when the list nears its bottom and reports the loaded
 * count below it.
 *
 * There is deliberately no Select All row. It could only ever take the page
 * that happens to be loaded, and a control that says "all" while taking a
 * fraction of the total is worse than none; the status line reports the count
 * instead.
 */
function AccountsField({
  emptyText,
  hasMore,
  help,
  inputId,
  isLoading,
  label,
  listId,
  loadingMoreText,
  onChange,
  onLoadMore,
  onRemove,
  onSearch,
  options,
  removeAriaLabel,
  pagedText,
  searchPlaceholder,
  selected,
  toggleAriaLabel,
  total,
  zIndex,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  // The panel, held in state rather than a ref so the effect below runs as soon
  // as it exists: Popover mounts its content a tick after `visible` flips, so an
  // effect keyed on isOpen alone runs while the panel is still absent and, its
  // dependencies unchanged, never runs again.
  //
  // The panel, not the list. Resolving the <ul> here would cache it for the
  // panel's whole life, and the <ul> comes and goes underneath: a search
  // matching nothing renders the empty state in its place, so clearing that
  // search mounts a new <ul> while the listener sits on the detached old one.
  const [panelNode, setPanelNode] = useState(null);
  const panelRef = useCallback((node) => setPanelNode(node), []);

  const { draft, handleChange, items, selectedIds } = useStagedSelection({
    isOpen,
    options,
    selected,
  });

  const Chevron = isOpen ? ChevronUp : ChevronDown;

  const close = () => {
    setIsOpen(false);

    // Clearing the box has to clear the filter behind it too. Otherwise the
    // next open shows an empty search box over a still-narrowed list, with a
    // status line describing the abandoned search.
    if (query !== '') onSearch('');

    setQuery('');
  };

  const apply = (values) => {
    onChange(values);
    close();
  };

  // Typing filters, and opens the menu if it was shut — otherwise the first
  // keystroke would narrow a list nobody can see.
  const search = (value) => {
    setQuery(value);
    setIsOpen(true);
    onSearch(value);
  };

  // Clicking the field's chrome puts the caret in the input, which is what
  // makes the whole box behave like one field rather than a box with an input
  // in it.
  const focusInput = () => {
    if (inputRef.current) inputRef.current.focus();
  };

  /**
   * Ask for the next page as the list nears its bottom.
   *
   * The listener goes on OptionsList's own scrolling <ul>: it already carries
   * `max-height: 310px` and `overflow: auto`, so it is the element that
   * scrolls. Found by query rather than by prop because OptionsList keeps its
   * container ref in internal state and forwards no way in.
   *
   * Guarded on scrollHeight because jsdom lays nothing out and reports every
   * dimension as 0, which would otherwise read as "already at the bottom" and
   * fetch as soon as the menu opened.
   */
  useEffect(() => {
    if (!panelNode || !hasMore) return undefined;

    const list = panelNode.querySelector('ul');
    if (!list) return undefined;

    const onScroll = () => {
      if (list.scrollHeight <= 0) return;

      const remaining = list.scrollHeight - list.scrollTop - list.clientHeight;

      if (remaining <= LOAD_MORE_THRESHOLD_PX) onLoadMore();
    };

    list.addEventListener('scroll', onScroll);
    return () => list.removeEventListener('scroll', onScroll);
  }, [hasMore, isLoading, onLoadMore, options.length, panelNode]);

  // Labels come from the applied selection, so the trigger keeps naming the
  // accounts even after a search narrows the options out from under them.
  const selectedLabels = selected.map((value) => {
    const option = options.find((item) => String(item.value) === String(value));
    return { value, label: option ? option.label : String(value) };
  });

  return (
    <FieldGroup>
      <Popover
        hideOnClickOutside
        onRequestHide={close}
        placement="bottom-start"
        showArrow={false}
        visible={isOpen}
        zIndex={zIndex}
        target={
          <AccountsTrigger $active={isOpen} onClick={focusInput}>
            <AccountsTriggerBody>
              <AccountsLabel>
                <P
                  as="label"
                  color="alaskanHusky"
                  htmlFor={inputId}
                  size="small"
                  style={{ margin: 0 }}
                >
                  {label}
                </P>
                <RequiredIcon />
              </AccountsLabel>
              <AccountsTags>
                {selectedLabels.map(({ label: optionLabel, value }) => (
                  <Tag
                    key={value}
                    size="small"
                    text={optionLabel}
                    // Dismissible only where the caller asks for it. The Create
                    // User wizard stages its selection behind the panel's APPLY,
                    // so a second way to drop one would bypass that; the access
                    // modals have no APPLY and the design gives their tags an x
                    // (Figma 1301:21873).
                    {...(onRemove
                      ? {
                          action: () => onRemove(value),
                          actionIcon: Times,
                          actionAttributes: {
                            'aria-label': removeAriaLabel(optionLabel),
                            role: 'button',
                          },
                        }
                      : {})}
                  />
                ))}
                <AccountsInput
                  ref={inputRef}
                  aria-autocomplete="list"
                  aria-controls={listId}
                  aria-expanded={isOpen}
                  autoComplete="off"
                  id={inputId}
                  // Dropped once accounts are tagged: by then the field has
                  // visibly been used, and the tags need the room.
                  placeholder={
                    selectedLabels.length === 0 ? searchPlaceholder : undefined
                  }
                  role="combobox"
                  value={query}
                  onChange={(event) => search(event.target.value)}
                  onFocus={() => setIsOpen(true)}
                />
              </AccountsTags>
            </AccountsTriggerBody>
            <AccountsChevron
              type="button"
              aria-label={toggleAriaLabel}
              // The input owns focus; the chevron only toggles, so it must not
              // take focus away and reopen the menu through onFocus.
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                setIsOpen((open) => !open);
              }}
            >
              <Chevron size="small" />
            </AccountsChevron>
          </AccountsTrigger>
        }
      >
        <AccountsPanel ref={panelRef}>
          {options.length === 0 ? (
            <AccountsEmpty>
              <P shade={1}>{isLoading ? loadingMoreText : emptyText}</P>
            </AccountsEmpty>
          ) : (
            <SelectableOptionsList
              multiple
              id={listId}
              items={items}
              // OptionsList's <ul> is capped at 340px wide by default, which
              // left the rows and their scrollbar stopping short of the
              // panel's right edge with a blank strip beside them. KeyDS
              // Select overrides the same two for the same reason.
              maxWidth="100%"
              minWidth="100%"
              // OptionsList defaults pointerEvents to "none", which leaves the
              // rows unclickable.
              pointerEvents="auto"
              selected={selectedIds}
              onChange={handleChange}
              // This logs "Invalid prop `actions`" whatever you pass it,
              // including the library's own documented shapes — its propType
              // is declared through react-desc's oneOfType, which
              // mis-validates. The Footer reads left/right at runtime and
              // renders correctly.
              actions={{
                left: [
                  {
                    text: 'Apply',
                    size: 'small',
                    onClick: () => apply(draft),
                  },
                  {
                    text: 'Cancel',
                    kind: 'secondary',
                    size: 'small',
                    onClick: close,
                  },
                ],
                right: [
                  {
                    text: 'Clear',
                    kind: 'secondary',
                    variant: 'ghost',
                    size: 'small',
                    onClick: () => apply([]),
                  },
                ],
              }}
            />
          )}
          {/* Below the list rather than as a row inside it: OptionsList's
              items are all selectable, so a status row would be tickable.

              This is what tells the admin the list is partial — without it, a
              loaded page can look like the whole list. */}
          {options.length > 0 && (isLoading || hasMore) && (
            <AccountsStatus>
              <P color="alaskanHusky" size="small" style={{ margin: 0 }}>
                {isLoading ? loadingMoreText : pagedText(options.length, total)}
              </P>
            </AccountsStatus>
          )}
        </AccountsPanel>
      </Popover>
      {/* What TextField and Select render for `helpMessage`: InputField's own
          HelpMessage is a <Message intent="help">. Going through the same
          component is what gives this line the filled QuestionCircleF in
          Licorice Mousse that hand-building it would miss. */}
      <Message intent="help">{help}</Message>
    </FieldGroup>
  );
}

AccountsField.propTypes = {
  emptyText: PropTypes.string,
  /** Whether pages remain beyond `options`; drives both scrolling and the label. */
  hasMore: PropTypes.bool,
  help: PropTypes.string.isRequired,
  inputId: PropTypes.string,
  isLoading: PropTypes.bool,
  label: PropTypes.string.isRequired,
  listId: PropTypes.string,
  loadingMoreText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  /** Asked for the next page; safe to call repeatedly. */
  onLoadMore: PropTypes.func,
  /**
   * Drops one value straight from its tag. Omit to render plain tags, which is
   * what a field whose selection is staged behind APPLY wants.
   */
  onRemove: PropTypes.func,
  onSearch: PropTypes.func.isRequired,
  /** The pages loaded so far, not every account the viewer may pick from. */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  pagedText: PropTypes.func,
  removeAriaLabel: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  selected: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  toggleAriaLabel: PropTypes.string,
  total: PropTypes.number,
  zIndex: PropTypes.number,
};

AccountsField.defaultProps = {
  emptyText: 'No accounts available.',
  hasMore: false,
  inputId: 'accounts-field-input',
  isLoading: false,
  listId: 'accounts-field-list',
  loadingMoreText: 'Loading more accounts...',
  onLoadMore: () => {},
  onRemove: undefined,
  options: [],
  pagedText: (loaded, total) => `Showing ${loaded} of ${total} accounts`,
  removeAriaLabel: (label) => `Remove ${label}`,
  searchPlaceholder: undefined,
  selected: [],
  toggleAriaLabel: 'Show account options',
  total: 0,
  zIndex: undefined,
};

export default AccountsField;
